/**
 * The studio.
 *
 * The whole product is here: assemble the images it works from, say what to do
 * with them, and render. Every control on this screen maps to something the
 * image service actually takes — a prompt, 1-10 images, a shape, a size, a file
 * format. Nothing here is decorative.
 *
 * The inputs strip IS the images array, in send order, numbered so the prompt's
 * clauses line up with it. The "what gets sent" panel shows the literal string
 * we post, because the client compiles it — there is no second, hidden prompt.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { api, downloadMedia, type Aspect, type Format, type InputSpec, type LibraryItem, type Render, type Resolution, type Character, type Swatch } from '../lib/api';
import { ASPECTS, ROLES, compile, dimensions, fileBase, isAuto, money, priceUsd, roleForCategory, type Role } from '../lib/compose';
import Img from '../components/Img';
import Catalog from '../components/Catalog';
import DownloadButton from '../components/DownloadButton';
import FluidCloudMark from '../components/FluidCloudMark';
import { openFluidCloudPicker, fetchPicked } from '../lib/fluidcloudPicker';
import './studio.css';

/** One row of the inputs strip. */
interface StudioInput {
  key: string;
  spec: InputSpec;
  src: string | null;
  label: string;
  role: Role;
  locked?: boolean;
}

export interface StudioProps {
  characters: Character[];
  library: LibraryItem[];
  activeCharacterId: string | null;
  onCharacterChange: (id: string | null) => void;
  onRendered: () => void;
  /**
   * Hidden rather than unmounted. Switching tabs used to destroy this component,
   * and with it the whole composition — inputs, prompt, the batch being polled.
   * Losing an assembled shot by glancing at the gallery is not acceptable, so
   * the studio stays mounted for the life of the session.
   */
  hidden?: boolean;
  /** An item sent over from the Library screen, consumed once on arrival. */
  pendingInput?: { item: LibraryItem; swatch: Swatch } | null;
  onPendingConsumed?: () => void;
}


export default function Studio({
  characters, library, activeCharacterId, onCharacterChange, onRendered,
  hidden, pendingInput, onPendingConsumed,
}: StudioProps) {
  const character = characters.find((s) => s.id === activeCharacterId) ?? null;

  const [extra, setExtra] = useState<StudioInput[]>([]);
  const [prompt, setPrompt] = useState('');
  const [aspect, setAspect] = useState<Aspect>('3:4');
  const [resolution, setResolution] = useState<Resolution>('2k');
  const [format, setFormat] = useState<Format>('jpeg');
  const [count, setCount] = useState(1);

  const [picking, setPicking] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const [batch, setBatch] = useState<Render[] | null>(null);
  const [batchId, setBatchId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chosen, setChosen] = useState(0);
  const [preview, setPreview] = useState<{ src: string | null; label: string; name: string } | null>(null);
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [dropKey, setDropKey] = useState<string | null>(null);
  const [fileOver, setFileOver] = useState(false);
  const uploadRef = useRef<HTMLInputElement>(null);
  const stripUploadRef = useRef<HTMLInputElement>(null);

  /**
   * A character's identity always leads, in a fixed order, and can't be
   * reordered away.
   *
   * The four built references are what goes out — they are the same four shots
   * for every character, so nothing has to be selected here and the wrong
   * reference can't be sent. A character with no references yet falls back to
   * its own photos, which is what every character looked like before reference
   * sets existed.
   */
  const characterInputs: StudioInput[] = useMemo(() => {
    if (!character) return [];
    const refs = character.references.filter((r) => r.status === 'done' && r.ready);
    const source = refs.length > 0
      ? refs.map((r) => ({ id: r.id, label: r.label, src: r.src }))
      : character.photos.filter((p) => p.ready).map((p) => ({ id: p.id, label: p.label || 'photo', src: p.src }));
    return source.map((p) => ({
      key: `character:${p.id}`,
      spec: { origin: 'character', photoId: p.id, role: 'none', label: `${character.name} — ${p.label}` },
      src: p.src,
      label: `${character.name} — ${p.label}`,
      role: 'none' as Role,
      locked: true,
    }));
  }, [character]);

  const inputs = useMemo(() => [...characterInputs, ...extra], [characterInputs, extra]);
  const compiled = compile(prompt, inputs, character?.description);
  /* On 'auto' the shape is the service's call, read off image 1 — so there are
     no dimensions to quote and the readout must not invent any. */
  const auto = isAuto(aspect);
  const [w, h] = dimensions(aspect, resolution);
  const per = priceUsd(resolution, inputs.length);
  const total = per * count;
  const canRender = inputs.length > 0 && compiled.trim().length > 0 && !busy;

  /* Poll while anything in the batch is unfinished. */
  useEffect(() => {
    if (!batchId) return;
    let active = true;
    const tick = async () => {
      try {
        const res = await api.get<{ renders: Render[] }>(`/renders/batch/${batchId}`);
        if (!active) return;
        setBatch(res.renders);
        const settled = res.renders.every((r) => ['done', 'failed', 'cancelled'].includes(r.status));
        if (settled) { setBusy(false); setBatchId(null); onRendered(); }
      } catch {
        if (active) { setBusy(false); setBatchId(null); }
      }
    };
    const timer = setInterval(() => void tick(), 1500);
    void tick();
    return () => { active = false; clearInterval(timer); };
  }, [batchId, onRendered]);

  function addInput(next: Omit<StudioInput, 'key'>) {
    if (inputs.length >= 10) return;
    setExtra((prev) => [...prev, { ...next, key: `${next.spec.origin}:${Date.now()}:${Math.round(performance.now())}` }]);
  }

  /* "Send to studio" from the Library screen lands here. */
  useEffect(() => {
    if (!pendingInput) return;
    const { item, swatch } = pendingInput;
    const role = roleForCategory(item.category);
    const label = `${item.name}${swatch.name ? ` · ${swatch.name}` : ''}`;
    addInput({ spec: { origin: 'library', swatchId: swatch.id, role, label }, src: swatch.src, label, role });
    onPendingConsumed?.();
    // addInput is stable enough here; the pending object is the real trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingInput]);

  function setRole(key: string, role: Role) {
    setExtra((prev) => prev.map((i) => (i.key === key ? { ...i, role, spec: { ...i.spec, role } } : i)));
  }

  /**
   * Reordering. Drag is the primary gesture, but this stays keyboard-reachable:
   * focus a tile and use ← / →. Dropping the arrow buttons shouldn't mean
   * dropping the only way to reorder without a mouse.
   */
  function move(key: string, dir: -1 | 1) {
    setExtra((prev) => {
      const i = prev.findIndex((x) => x.key === key);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j]!, next[i]!];
      return next;
    });
  }

  /** Move `from` so it sits where `to` is, both keyed within `extra`. */
  function reorder(fromKey: string, toKey: string) {
    if (fromKey === toKey) return;
    setExtra((prev) => {
      const from = prev.findIndex((x) => x.key === fromKey);
      const to = prev.findIndex((x) => x.key === toKey);
      if (from < 0 || to < 0) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved!);
      return next;
    });
  }

  /** Import from the user's FluidCloud Drive: pick → fetch → the normal upload. */
  async function importFromCloud() {
    setError(null);
    try {
      await openFluidCloudPicker({
        multiple: true,
        accept: 'image/*',
        onPicked: (items) => {
          void (async () => {
            for (const item of items.slice(0, Math.max(0, 10 - inputs.length))) {
              try {
                setUploading(item.name);
                const file = await fetchPicked(item);
                await onUpload(file);
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Import failed');
                break;
              }
            }
            setUploading(null);
          })();
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not open FluidCloud');
    }
  }

  /**
   * A drag carrying files is a photo being added; a drag carrying nothing is a
   * tile being reordered. Both land on the same elements, so every handler has
   * to say which one it wants or a dropped file reorders the strip instead.
   */
  function isFileDrag(e: React.DragEvent): boolean {
    return Array.from(e.dataTransfer.types ?? []).includes('Files');
  }

  /** Drop several at once — a batch of photos is the normal case, not the rare one. */
  async function onUploadFiles(files: FileList | File[]) {
    const room = Math.max(0, 10 - inputs.length);
    const images = Array.from(files).filter((f) => f.type.startsWith('image/')).slice(0, room);
    if (images.length === 0) return;
    for (const file of images) await onUpload(file);
  }

  async function onUpload(file: File) {
    // An ad-hoc upload becomes a one-swatch library item, so it's reusable later
    // rather than vanishing after one render.
    setError(null);
    setUploading(file.name);
    try {
      const item = await api.post<{ item: LibraryItem }>('/library', {
        name: file.name.replace(/\.[^.]+$/, '').slice(0, 80) || 'Upload',
        category: 'objects',
        subcategory: 'Uploads',
      });
      const form = new FormData();
      form.append('file', file);
      form.append('name', 'As shot');
      const { swatch } = await api.upload<{ swatch: Swatch }>(`/library/${item.item.id}/swatches`, form);
      addInput({
        spec: { origin: 'library', swatchId: swatch.id, role: 'none', label: item.item.name },
        src: swatch.src, label: item.item.name, role: 'none',
      });
      setPicking(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(null);
    }
  }

  /** Save an image to disk. The render is the point of the screen — it should not
      have to be right-clicked out of a blob URL. */
  async function save(src: string | null, base: string) {
    if (!src) return;
    setError(null);
    try {
      await downloadMedia(src, base);
    } catch {
      setError('Could not save that image');
    }
  }

  async function render() {
    setError(null);
    setBusy(true);
    setChosen(0);
    try {
      const body = {
        prompt: compiled,
        inputs: inputs.map((i) => ({ ...i.spec, role: i.role })) as InputSpec[],
        aspect, resolution, format, count,
        ...(character ? { characterId: character.id } : {}),
      };
      const res = await api.post<{ batchId: string; renders: Render[] }>('/renders', body);
      setBatch(res.renders);
      setBatchId(res.batchId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start the render');
      setBusy(false);
    }
  }

  async function cancel() {
    if (!batchId) return;
    try { await api.post(`/renders/batch/${batchId}/cancel`); } catch { /* the poll will catch up */ }
  }

  const done = (batch ?? []).filter((r) => r.status === 'done');
  const showing = done[Math.min(chosen, done.length - 1)];

  return (
    <div className="studio" style={hidden ? { display: 'none' } : undefined}>
      <div className="stage">
        <div className="stage-top">
          <span className="eyebrow">Result</span>
          <span className="pill mono" title={auto ? 'The service picks the ratio closest to image 1' : undefined}>
            {auto ? 'auto · from image 1' : `${w} × ${h}`}
          </span>
          {showing?.tookMs && <span className="pill mono">{(showing.tookMs / 1000).toFixed(1)}s</span>}
          {showing && (
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <DownloadButton
                title="Download this image"
                onClick={() => void save(showing.src, fileBase(showing.prompt, showing.id))}
              />
              <button className="btn btn-sm" onClick={() => {
                addInput({
                  spec: { origin: 'result', renderId: showing.id, role: 'edit', label: 'last result' },
                  src: showing.src, label: 'last result', role: 'edit',
                });
                setPrompt('Same image, but ');
              }}>Refine this</button>
              <button className="btn btn-sm" onClick={() => { setResolution('2k'); }}>Redo at 2K</button>
              <button className="btn btn-sm" onClick={() => void render()}>More like it</button>
            </div>
          )}
        </div>

        <div className="canvas-wrap">
          {!showing && !busy && (
            <div className="canvas-empty">
              <div style={{ fontSize: 24, opacity: .3 }}>◫</div>
              <div style={{ fontSize: 13.5 }}>Nothing rendered yet</div>
              <div style={{ fontSize: 12, maxWidth: '38ch', color: 'var(--fd-faintest)' }}>
                Add the images it works from, say what to do with them, and render.
              </div>
            </div>
          )}
          {showing && (
            <button
              className="canvas-peek"
              title="See it full size"
              onClick={() => setPreview({
                src: showing.src,
                label: `Render · ${auto ? 'auto' : `${w} × ${h}`}`,
                name: fileBase(showing.prompt, showing.id),
              })}
            >
              <Img
                className="fit"
                src={showing.src}
                alt="Render"
                // Fill the canvas: height drives it, aspect-ratio derives the
                // width, and max-width pulls it back on a narrow window. On
                // 'auto' we don't know the shape, so let the image's own
                // proportions size the frame instead of guessing a square.
                style={{ height: '100%', aspectRatio: auto ? 'auto' : `${w} / ${h}`, maxWidth: '100%' }}
              />
            </button>
          )}

          {batch && busy && (
            <div className="calls">
              {batch.map((r) => (
                <div className="call-row" key={r.id}>
                  <span className="n">{r.seq + 1} of {batch.length}</span>
                  <span className="bar">
                    <span style={{ width: r.status === 'done' ? '100%' : r.status === 'running' ? '80%' : '4%' }} />
                  </span>
                  <span className="st" style={{ color: r.status === 'done' ? 'var(--fd-ok)' : r.status === 'failed' ? 'var(--fd-bad)' : undefined }}>
                    {r.status === 'done' && r.tookMs ? `${(r.tookMs / 1000).toFixed(1)}s` : r.status}
                  </span>
                </div>
              ))}
              <button className="btn btn-sm" style={{ marginTop: 4 }} onClick={() => void cancel()}>Cancel</button>
            </div>
          )}
        </div>

        {(batch ?? []).some((r) => r.status === 'failed') && (
          <div className="notice bad">
            {batch!.filter((r) => r.status === 'failed').length} of {batch!.length} didn't finish.{' '}
            {batch!.find((r) => r.error)?.error}
          </div>
        )}

        {done.length > 1 && (
          <div className="results">
            {done.map((r, i) => (
              <button key={r.id} aria-pressed={i === Math.min(chosen, done.length - 1)} onClick={() => setChosen(i)}>
                <Img src={r.src} alt={`Variation ${i + 1}`} style={{ width: 62, aspectRatio: '3/4' }} />
              </button>
            ))}
          </div>
        )}

        <div className="stage-bottom">
        <div className="inputs">
          <div className="inputs-head">
            <span className="eyebrow">What it works from</span>
            <span className="pill mono">{inputs.length} / 10</span>
            <span className="faint" style={{ fontSize: 11.5, marginLeft: 6 }}>
              Order matters — the prompt refers to them by number.
            </span>
          </div>
          <div
            className={`strip${fileOver ? ' file-over' : ''}`}
            onDragOver={(e) => {
              if (!isFileDrag(e)) return;          // a tile reorder — leave it to the slots
              e.preventDefault();
              e.dataTransfer.dropEffect = 'copy';
              setFileOver(true);
            }}
            onDragLeave={(e) => {
              // Only when the pointer actually leaves the strip, not on every
              // child it crosses on the way in.
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFileOver(false);
            }}
            onDrop={(e) => {
              if (!isFileDrag(e)) return;
              e.preventDefault();
              setFileOver(false);
              void onUploadFiles(e.dataTransfer.files);
            }}
          >
            {inputs.map((input, i) => (
              <div
                key={input.key}
                className={[
                  'slot',
                  input.locked ? 'locked' : '',
                  dragKey === input.key ? 'dragging' : '',
                  dropKey === input.key && dragKey !== input.key ? 'dropping' : '',
                ].filter(Boolean).join(' ')}
                draggable={!input.locked}
                tabIndex={input.locked ? -1 : 0}
                onDragStart={(e) => { setDragKey(input.key); e.dataTransfer.effectAllowed = 'move'; }}
                onDragOver={(e) => {
                  if (input.locked || !dragKey) return;
                  e.preventDefault();               // without this, drop never fires
                  e.dataTransfer.dropEffect = 'move';
                  setDropKey(input.key);
                }}
                onDragLeave={() => setDropKey((k) => (k === input.key ? null : k))}
                onDrop={(e) => {
                  if (isFileDrag(e)) return;      // dropped photos belong to the strip
                  e.preventDefault();
                  if (dragKey && !input.locked) reorder(dragKey, input.key);
                  setDragKey(null); setDropKey(null);
                }}
                onDragEnd={() => { setDragKey(null); setDropKey(null); }}
                onKeyDown={(e) => {
                  if (input.locked) return;
                  if (e.key === 'ArrowLeft') { e.preventDefault(); move(input.key, -1); }
                  if (e.key === 'ArrowRight') { e.preventDefault(); move(input.key, 1); }
                }}
              >
                <div className="top">
                  <button
                    className="peek"
                    title="See it full size"
                    onClick={() => setPreview({ src: input.src, label: input.label, name: fileBase(input.label) })}
                  >
                    <Img src={input.src} alt={input.label} style={{ width: '100%', aspectRatio: '1', borderRadius: 0 }} />
                  </button>
                  <span className="idx">{i + 1}</span>
                  {!input.locked && (
                    <button className="kill" title="Remove" onClick={() => setExtra((p) => p.filter((x) => x.key !== input.key))}>×</button>
                  )}
                </div>
                <div className="btm">
                  <div className="origin" title={input.label}>{input.label}</div>
                  {!input.locked && <div className="grip" aria-hidden="true">⠿ drag to reorder</div>}
                  {/* Last in the tile, and pinned to its bottom edge by CSS: the
                      tiles stretch to a common height, so a dropdown that sat
                      right under its picture landed at a different height on
                      every tile. */}
                  <select
                    value={input.role}
                    onChange={(e) => setRole(input.key, e.target.value as Role)}
                    disabled={input.locked}
                  >
                    {ROLES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                  </select>
                </div>
              </div>
            ))}
            {inputs.length < 10 && (
              <>
                {/* One tile, two sources. The body takes a photo off the machine —
                    clicked or dropped — and the strip along the bottom goes to
                    FluidCloud. Nested buttons are invalid HTML, so this is a div
                    holding two real buttons rather than a button inside a button. */}
                <div className="slot-src">
                  <button
                    className="main"
                    onClick={() => stripUploadRef.current?.click()}
                    disabled={Boolean(uploading)}
                  >
                    {uploading ? <span className="spin" /> : <span style={{ fontSize: 21 }}>+</span>}
                    <span>{uploading ? 'Adding…' : 'Add photo'}</span>
                    <span className="sub">{uploading ? uploading : 'drag in, or browse\nyour machine'}</span>
                  </button>
                  <button className="cloud" onClick={() => void importFromCloud()} disabled={Boolean(uploading)}>
                    <FluidCloudMark size={17} />
                    FluidCloud
                  </button>
                </div>
                <button className="slot-add" onClick={() => setPicking(true)}>
                  <span style={{ fontSize: 19 }}>+</span>
                  <span>Add from library</span>
                  <span className="mono" style={{ fontSize: 9.5, color: 'var(--fd-faintest)' }}>outfits · places · past results</span>
                </button>
              </>
            )}
          </div>
          <p className="faint" style={{ fontSize: 11.5, margin: '10px 0 0', lineHeight: 1.55 }}>
            The dropdown on each one writes a line into the prompt. Set it to <b className="muted">nothing</b> to
            attach an image without saying anything about it. Drag a tile to reorder it, or
            focus one and use ← →. Click a thumbnail to see it full size.
          </p>
          {error && !picking && (
            <div className="notice bad" role="alert" style={{ marginTop: 10 }}>{error}</div>
          )}
          <input
            ref={stripUploadRef} type="file" accept="image/jpeg,image/png,image/webp" hidden multiple
            onChange={(e) => {
                // Copy the FileList out BEFORE resetting the input: it is LIVE,
                // and clearing value empties the very list we are holding, so
                // the handler saw zero files and did nothing — silently.
                const picked = Array.from(e.target.files ?? []);
                e.target.value = '';
                if (picked.length) void onUploadFiles(picked);
              }}
          />
        </div>

        <div className="composer">
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Describe the shot…"
          />
          <div className="composer-foot">
            <span className="eyebrow" style={{ marginRight: 2 }}>Starters</span>
            {[
              'Standing against a plain studio backdrop, soft key light from the left, waist up.',
              'Full length, walking toward the camera on a city street in daylight.',
              'Close crop on the face, natural window light, shallow depth of field.',
            ].map((p, i) => (
              <button key={i} className="chip" onClick={() => setPrompt(p)}>
                {['Studio portrait', 'Full length', 'Close-up'][i]}
              </button>
            ))}
            <span className="mono faint" style={{ marginLeft: 'auto', fontSize: 11 }}>{compiled.length} chars sent</span>
          </div>
          <details className="sent" open>
            <summary>What gets sent</summary>
            <pre className="req">
              <span className="k">prompt</span>  {compiled || '(nothing yet)'}{'\n\n'}
              <span className="k">images</span>  {inputs.length}{'\n'}
              <span className="k">aspect</span>  {auto ? '(not sent — the service follows image 1)' : aspect}{'\n'}
              <span className="k">resolution</span> {resolution}{'\n'}
              <span className="k">file</span>    {format}{'\n\n'}
              <span className="k">×{count}</span>      <span className="v">{money(total)}</span> total
            </pre>
          </details>
        </div>
        </div>
      </div>

      <aside className="controls">
        <div className="controls-scroll">
          <div className="sect">
            <div className="sect-head"><span className="eyebrow" style={{ flex: 1 }}>Character</span></div>
            <select className="field" value={activeCharacterId ?? ''} onChange={(e) => onCharacterChange(e.target.value || null)}>
              <option value="">No character</option>
              {characters.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <p className="faint" style={{ fontSize: 11.5, margin: '9px 0 0', lineHeight: 1.5 }}>
              {character
                ? `Their photos go in first, and their description gets added to the prompt.`
                : `Pick one to anchor every render to the same person.`}
            </p>
          </div>

          <div className="sect">
            <div className="sect-head"><span className="eyebrow">Output</span></div>
            <div className="row">
              <span className="lbl">Aspect ratio</span>
              <select className="field" style={{ width: 142 }} value={aspect} onChange={(e) => setAspect(e.target.value as Aspect)}>
                {ASPECTS.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
              </select>
            </div>
            <div className="row">
              <span className="lbl">Resolution</span>
              <div className="seg">
                {(['1k', '2k'] as const).map((r) => (
                  <button key={r} aria-pressed={resolution === r} onClick={() => setResolution(r)}>{r.toUpperCase()}</button>
                ))}
              </div>
            </div>
            <div className="row">
              <span className="lbl">File</span>
              <div className="seg">
                {(['jpeg', 'png'] as const).map((f) => (
                  <button key={f} aria-pressed={format === f} onClick={() => setFormat(f)}>{f.toUpperCase()}</button>
                ))}
              </div>
            </div>
            <div className="row">
              <span className="lbl">How many</span>
              <div className="seg">
                {[1, 2, 4, 8].map((n) => (
                  <button key={n} aria-pressed={count === n} onClick={() => setCount(n)}>{n}</button>
                ))}
              </div>
            </div>
            <p className="faint" style={{ fontSize: 11.5, margin: '8px 0 0', lineHeight: 1.5 }}>
              Each one is a separate request — {count} image{count === 1 ? '' : 's'} means {count} request{count === 1 ? '' : 's'}.
            </p>
          </div>
        </div>

        <div className="cost">
          {error && <div className="notice bad">{error}</div>}
          <div className="cost-line">
            <span className="amt">{money(total)}</span>
            <span className="faint" style={{ fontSize: 12 }}>
              {count} × {money(per)} · {resolution === '2k' ? '0.090' : '0.045'} + {Math.max(0, inputs.length - 1)} extra
            </span>
          </div>
          <button className="btn btn-primary" style={{ width: '100%', padding: 10 }} disabled={!canRender} onClick={() => void render()}>
            {busy ? <><span className="spin" /> Rendering…</> : `Render ${count}`}
          </button>
        </div>
      </aside>

      {preview && (
        <>
          <div className="scrim" onClick={() => setPreview(null)} />
          <div className="lightbox" role="dialog" aria-label={preview.label}>
            <div className="lightbox-head">
              <span style={{ flex: 1, fontSize: 13 }}>{preview.label}</span>
              <DownloadButton title="Download this image" onClick={() => void save(preview.src, preview.name)} />
              <button className="btn btn-sm" onClick={() => setPreview(null)}>Close</button>
            </div>
            <Img src={preview.src} alt={preview.label} style={{ maxHeight: '76vh', width: 'auto' }} />
          </div>
        </>
      )}

      {picking && (
        <>
          <div className="scrim" onClick={() => setPicking(false)} />
          <div className="modal">
            <div className="modal-head">
              <span style={{ flex: 1, fontSize: 14 }}>Add an input</span>
              <button className="btn btn-sm" onClick={() => uploadRef.current?.click()}>Upload</button>
              <button className="btn btn-sm" onClick={() => void importFromCloud()}>From FluidCloud</button>
              <button className="btn btn-sm" onClick={() => setPicking(false)}>Close</button>
              <input
                ref={uploadRef} type="file" accept="image/jpeg,image/png,image/webp" hidden
                onChange={(e) => { const f = e.target.files?.[0]; if (f) void onUpload(f); e.target.value = ''; }}
              />
            </div>
            <div className="modal-body">
              {/* The picker covers the whole stage, so an error rendered down in
                  the cost bar is invisible — which is exactly why a failed
                  upload looked like nothing happening at all. It belongs here. */}
              {uploading && (
                <div className="notice" style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className="spin" /> Uploading {uploading}…
                </div>
              )}
              {error && !uploading && (
                <div className="notice bad" role="alert" style={{ marginBottom: 12 }}>{error}</div>
              )}
              <Catalog
                items={library}
                compact
                actionLabel="Add as input"
                hint={(item) => `Goes in as “${ROLES.find((r) => r.id === roleForCategory(item.category))?.label}”.`}
                onUse={(item, swatch) => {
                  const role = roleForCategory(item.category);
                  addInput({
                    spec: { origin: 'library', swatchId: swatch.id, role, label: `${item.name}${swatch.name ? ` · ${swatch.name}` : ''}` },
                    src: swatch.src,
                    label: `${item.name}${swatch.name ? ` · ${swatch.name}` : ''}`,
                    role,
                  });
                  setPicking(false);
                }}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
